import Chart from 'react-apexcharts';
import numeral from 'numeral';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Box, Card, Container, Grid, Typography, Avatar } from '@material-ui/core';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import EuroRoundedIcon from '@mui/icons-material/EuroRounded';
import PieChartRoundedIcon from '@mui/icons-material/PieChartRounded';
import DonutLargeRoundedIcon from '@mui/icons-material/DonutLargeRounded';

const formatEuro = (value) => numeral(value).format('0,000,000').replaceAll(',', ' ');

const StatCard = ({ icon, gradient, shadow, value, label }) => (
  <Card
    sx={{
      height: '100%',
      p: 2.5,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      borderRadius: '18px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.05)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 10px 20px -5px rgba(0, 0, 0, 0.12)'
      }
    }}
  >
    <Avatar
      variant="rounded"
      sx={{
        width: 52,
        height: 52,
        borderRadius: '14px',
        background: gradient,
        boxShadow: shadow
      }}
    >
      {icon}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: '#1e293b', lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
    </Box>
  </Card>
);

StatCard.propTypes = {
  icon: PropTypes.node,
  gradient: PropTypes.string,
  shadow: PropTypes.string,
  value: PropTypes.node,
  label: PropTypes.string
};

const ChartCard = ({ icon, gradient, shadow, title, subtitle, children }) => (
  <Card
    sx={{
      height: '100%',
      borderRadius: '20px',
      overflow: 'hidden',
      border: '1px solid #e2e8f0',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 3,
        py: 2.25,
        borderBottom: '1px solid #e2e8f0'
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          width: 40,
          height: 40,
          borderRadius: '12px',
          background: gradient,
          boxShadow: shadow
        }}
      >
        {icon}
      </Avatar>
      <Box>
        <Typography sx={{ fontWeight: 800, fontSize: '1.02rem', color: '#1e293b', letterSpacing: '-0.01em' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#64748b' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Card>
);

ChartCard.propTypes = {
  icon: PropTypes.node,
  gradient: PropTypes.string,
  shadow: PropTypes.string,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  children: PropTypes.node
};

const SessionStats = (props) => {
  const { customers, formation, customersFixed, ...other } = props;
  const [data, setData] = useState({ series: [{
    color: '#FFB547',
    data: 0,
    label: 'Inscrit'
  },
  {
    color: '#ff5f52',
    data: 0,
    label: 'En attente'
  },
  {
    color: '#7783DB',
    data: 0,
    label: 'Désinscription'
  }] });
  const [dataProgress, setDataProgress] = useState({ seriesProgress: [{
    color: '#FFB547',
    data: 0,
    label: 'Terminé',
    count: 0
  },
  {
    color: '#ff5f52',
    data: 0,
    label: 'En cours',
    count: 0
  },
  {
    color: '#7783DB',
    data: 0,
    label: 'Pas commencé',
    count: 0
  }] });
  const [total, setTotal] = useState(0);
  const theme = useTheme();

  const chartOptions = {
    chart: {
      background: 'transparent',
      stacked: false,
      toolbar: {
        show: false
      }
    },
    colors: data.series.map((item) => item.color),
    dataLabels: {
      enabled: false
    },
    labels: data.series.map((item) => item.label),
    legend: {
      show: false
    },
    stroke: {
      colors: [theme.palette.background.paper],
      width: 1
    },
    theme: {
      mode: theme.palette.mode
    }
  };

  const chartProgressOptions = {
    chart: {
      background: 'transparent',
      stacked: false,
      toolbar: {
        show: false
      }
    },
    colors: dataProgress.seriesProgress.map((itemProgress) => itemProgress.color),
    dataLabels: {
      enabled: false
    },
    labels: dataProgress.seriesProgress.map((itemProgress) => itemProgress.label),
    legend: {
      show: false
    },
    stroke: {
      colors: [theme.palette.background.paper],
      width: 1
    },
    theme: {
      mode: theme.palette.mode
    }
  };

  useEffect(() => {

    let totalInscrit = 0;
    let totalEnAttente = 0;
    let totalADécaler = 0;
    let tempTotal = 0;

    customers.forEach((customer) => {
      if (customer.status === 'Inscrit') {
        totalInscrit += customer.real_price;
        tempTotal += customer.real_price;
      } else
      if (customer.status === 'En attente') {
        totalEnAttente += customer.real_price;
        tempTotal += customer.real_price;
      } else
      if (customer.status === 'À décaler') {
        totalADécaler += customer.real_price;
      }
    });
    let countCustomersFinished = 0;
    let countCustomersBegun = 0;
    let countCustomersNotBegun = 0;
    customersFixed.forEach((customer) => {
      if (customer.progression === 100) {
        countCustomersFinished++;
      } else if (customer.progression < 100 && customer.progression > 0) {
        countCustomersBegun++;
      } else if (customer.progression === 0) {
        countCustomersNotBegun++;
      }
    });
    setTotal(tempTotal);
    setData({ series: [{
      color: '#FFB547',
      data: totalInscrit,
      label: 'Inscrit'
    },
    {
      color: '#ff5f52',
      data: totalEnAttente,
      label: 'En attente'
    },
    {
      color: '#7783DB',
      data: totalADécaler,
      label: 'Désinscription'
    }] });
    setDataProgress({ seriesProgress: [{
      color: '#16B84E',
      data: Math.round((countCustomersFinished / customersFixed.length) * 100 * 100) / 100,
      label: 'Terminé',
      count: countCustomersFinished
    },
    {
      color: '#FFCF39',
      data: Math.round((countCustomersBegun / customersFixed.length) * 100 * 100) / 100,
      label: 'En cours',
      count: countCustomersBegun
    },
    {
      color: '#ff5f52',
      data: Math.round((countCustomersNotBegun / customersFixed.length) * 100 * 100) / 100,
      label: 'Pas commencé',
      count: countCustomersNotBegun
    }] });
  }, [customers]);

  const chartSeries = data.series.map((item) => item.data);
  const chartProgressSeries = dataProgress.seriesProgress.map((itemProgress) => itemProgress.data);

  const accents = [
    { gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', shadow: '0 6px 16px rgba(59, 130, 246, 0.3)' },
    { gradient: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)', shadow: '0 6px 16px rgba(99, 102, 241, 0.3)' },
    { gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', shadow: '0 6px 16px rgba(139, 92, 246, 0.3)' }
  ];

  return (
    <Box
      {...other}
      sx={{
        backgroundColor: 'background.default',
        p: 3
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* ── Cartes de synthèse ── */}
          <Grid container spacing={3} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<EuroRoundedIcon sx={{ fontSize: '1.5rem' }} />}
                gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                shadow="0 6px 16px rgba(16, 185, 129, 0.3)"
                value={`${formatEuro(total.toFixed(2))} €`}
                label="Total potentiel"
              />
            </Grid>
            {data.series.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={item.label}>
                <StatCard
                  icon={<EuroRoundedIcon sx={{ fontSize: '1.5rem' }} />}
                  gradient={accents[index % accents.length].gradient}
                  shadow={accents[index % accents.length].shadow}
                  value={`${formatEuro(item.data)} €`}
                  label={item.label}
                />
              </Grid>
            ))}
          </Grid>

          {/* ── Graphiques ── */}
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <ChartCard
                icon={<PieChartRoundedIcon sx={{ fontSize: '1.25rem' }} />}
                gradient="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                shadow="0 6px 16px rgba(59, 130, 246, 0.3)"
                title="Session"
                subtitle={`Total potentiel ${formatEuro(total.toFixed(2))} €`}
              >
                <Chart
                  height="260"
                  options={chartOptions}
                  series={chartSeries}
                  type="pie"
                />
                <Box sx={{ mt: 2 }}>
                  {data.series.map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        p: 1.25,
                        borderRadius: '12px',
                        transition: 'background-color 0.2s',
                        '&:hover': { backgroundColor: 'rgba(148, 163, 184, 0.08)' }
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: item.color,
                          borderRadius: '4px',
                          height: 12,
                          width: 12,
                          boxShadow: `0 0 0 3px ${item.color}22`
                        }}
                      />
                      <Typography
                        sx={{ ml: 2, fontWeight: 700, color: '#334155' }}
                        variant="subtitle2"
                      >
                        {item.label}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Typography
                        sx={{ fontWeight: 800, color: '#1e293b' }}
                        variant="subtitle2"
                      >
                        {formatEuro(item.data)}
                        {' '}
                        €
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ChartCard
                icon={<DonutLargeRoundedIcon sx={{ fontSize: '1.25rem' }} />}
                gradient="linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)"
                shadow="0 6px 16px rgba(139, 92, 246, 0.3)"
                title="Répartition de l'avancée des apprenants"
              >
                <Chart
                  height="260"
                  options={chartProgressOptions}
                  series={chartProgressSeries}
                  type="pie"
                />
                <Box sx={{ mt: 2 }}>
                  {dataProgress.seriesProgress.map((itemProgress) => (
                    <Box
                      key={itemProgress.label}
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        p: 1.25,
                        borderRadius: '12px',
                        transition: 'background-color 0.2s',
                        '&:hover': { backgroundColor: 'rgba(148, 163, 184, 0.08)' }
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: itemProgress.color,
                          borderRadius: '4px',
                          height: 12,
                          width: 12,
                          boxShadow: `0 0 0 3px ${itemProgress.color}22`
                        }}
                      />
                      <Typography
                        sx={{ ml: 2, fontWeight: 700, color: '#334155' }}
                        variant="subtitle2"
                      >
                        {itemProgress.label}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Typography
                        sx={{ fontWeight: 800, color: '#1e293b' }}
                        variant="subtitle2"
                      >
                        {`${numeral(itemProgress.data).format('0,000,000').replaceAll(',', ' ')}% (${itemProgress.count} au total)`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </ChartCard>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

SessionStats.propTypes = {
  customers: PropTypes.array,
  formation: PropTypes.object,
  customersFixed: PropTypes.array
};

export default SessionStats;
